import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api-client";
import type { components } from "@/types/api.generated";
import { toast } from "sonner";

export interface KudosFilters {
  author_id?: string;
  objective_id?: string;
  kr_id?: string;
  cursor?: string;
  limit?: number;
}

export function useKudos(filters: KudosFilters = {}) {
  return useQuery({
    queryKey: ["kudos", filters],
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/v1/kudos", {
        params: { query: filters },
      });
      if (error) throw new Error(error.error.message);
      return data;
    },
  });
}

export function useKudosForObjective(objectiveId: string) {
  return useKudos({ objective_id: objectiveId });
}

export function useKudosForKeyResult(krId: string) {
  return useKudos({ kr_id: krId });
}

export function useCreateKudos() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: components["schemas"]["CreateKudosRequest"]) => {
      const { data, error } = await apiClient.POST("/v1/kudos", { body });
      if (error) throw new Error(error.error.message);
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["kudos"] });
      toast.success("Kudos given!");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

export function useDeleteKudos() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await apiClient.DELETE("/v1/kudos/{id}", {
        params: { path: { id } },
      });
      if (error) throw new Error(error.error.message);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["kudos"] });
      toast.success("Kudos deleted");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}
